import * as React from 'react';
import { ChevronDown, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import { runeLength } from '@/lib/copyValidation';
import type { CopyItem, CopyItemField, CopyItemFieldValue, CopyLocale } from '@/schemas/admin/copy';
import { LOCALE_DIR, preferMultiline } from './StringEditor';
import { LockedAction } from './StringListEditor';

type Item = CopyItem;

interface ObjectListEditorProps {
  id: string;
  locale: CopyLocale;
  value: readonly Item[];
  onChange: (next: Item[]) => void;
  baseline: readonly Item[];
  itemFields: readonly CopyItemField[];
  maxLen: number | null;
  /** Keyed by leaf path (`[2].body`), matching `validateCopyValue` issue fields. */
  fieldErrors?: Record<string, string>;
  label: React.ReactNode;
}

/**
 * The `object_list` shape — 25 keys, up to 20 fields across 5 items
 * (`forYou.posts`).
 *
 * Structural fields (`key`, `icon`, `href`, …) are join keys and component
 * lookups on the public site: `ContentPillars.tsx` does `ICONS[p.icon]` with no
 * fallback, so a "translated" icon name is a blank render. They are shown —
 * hiding them would make the item unrecognisable — but read-only, behind a
 * lock and a tooltip that says why, and collapsed by default so a 20-field
 * item stays readable.
 */
export function ObjectListEditor({
  id,
  locale,
  value,
  onChange,
  baseline,
  itemFields,
  maxLen,
  fieldErrors,
  label,
}: ObjectListEditorProps) {
  const { t } = useTranslation('copy');
  const dir = LOCALE_DIR[locale];
  const limit = maxLen && maxLen > 0 ? maxLen : null;
  const count = Math.max(value.length, baseline.length);

  // The manifest's field order is the frontend's order; fall back to whatever
  // the baseline item actually carries if a key predates `item_fields`.
  const fields: CopyItemField[] = React.useMemo(() => {
    if (itemFields.length > 0) return [...itemFields];
    const first = baseline[0] ?? value[0] ?? {};
    return Object.keys(first).map((name) => ({ name, role: 'text' as const }));
  }, [itemFields, baseline, value]);

  const textFields = fields.filter((f) => f.role === 'text');
  const structuralFields = fields.filter((f) => f.role === 'structural');

  function updateField(index: number, field: string, next: CopyItemFieldValue) {
    const items = Array.from({ length: count }, (_, i) => ({
      ...(baseline[i] ?? {}),
      ...(value[i] ?? {}),
    }));
    const target = items[index];
    if (!target) return;
    items[index] = { ...target, [field]: next };
    onChange(items);
  }

  /** One entry of a list-valued text field; the list length is locked. */
  function updateEntry(index: number, field: string, entry: number, next: string) {
    const current = value[index]?.[field] ?? baseline[index]?.[field];
    const list = Array.isArray(current) ? [...current] : [];
    list[entry] = next;
    updateField(index, field, list);
  }

  return (
    <div className="mb-4" role="group" aria-labelledby={`${id}-label`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p id={`${id}-label`} className="text-sm font-semibold text-[color:var(--color-fg)]">
          {label}
        </p>
        <span className="inline-flex items-center gap-1 text-xs text-[color:var(--color-fg-muted)]">
          <Lock className="size-3.5" aria-hidden />
          {t('editor.fixedCount', { count })}
        </span>
      </div>

      <ol className="space-y-3">
        {Array.from({ length: count }, (_, i) => {
          const item = value[i] ?? {};
          const base = baseline[i] ?? {};
          return (
            <li
              key={i}
              className="rounded-[var(--radius-lg)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 shadow-sm"
            >
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
                {t('editor.itemN', { n: i + 1 })}
              </p>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {textFields.map((field) => {
                  const current = item[field.name];
                  const baseValue = base[field.name];
                  if (Array.isArray(baseValue) || Array.isArray(current)) {
                    const entries = Array.isArray(current) ? current : [];
                    const baseEntries = Array.isArray(baseValue) ? baseValue : [];
                    const n = Math.max(entries.length, baseEntries.length);
                    return (
                      <fieldset key={field.name} className="space-y-2 md:col-span-2">
                        <legend
                          dir="ltr"
                          className="font-mono text-[11px] font-semibold text-[color:var(--color-fg-muted)]"
                        >
                          {field.name}
                        </legend>
                        {Array.from({ length: n }, (_, j) => (
                          <TextLeaf
                            key={j}
                            id={`${id}-${i}-${field.name}-${j}`}
                            label={`${field.name} · ${j + 1}`}
                            dir={dir}
                            limit={limit}
                            value={entries[j] ?? ''}
                            baseValue={baseEntries[j] ?? ''}
                            error={fieldErrors?.[`[${i}].${field.name}[${j}]`]}
                            onChange={(next) => updateEntry(i, field.name, j, next)}
                          />
                        ))}
                      </fieldset>
                    );
                  }
                  return (
                    <TextLeaf
                      key={field.name}
                      id={`${id}-${i}-${field.name}`}
                      label={field.name}
                      dir={dir}
                      limit={limit}
                      value={typeof current === 'string' ? current : ''}
                      baseValue={typeof baseValue === 'string' ? baseValue : ''}
                      error={fieldErrors?.[`[${i}].${field.name}`]}
                      onChange={(next) => updateField(i, field.name, next)}
                    />
                  );
                })}
              </div>

              {structuralFields.length > 0 ? (
                <StructuralFields
                  id={`${id}-${i}-structural`}
                  fields={structuralFields}
                  item={{ ...base, ...item }}
                />
              ) : null}
            </li>
          );
        })}
      </ol>

      <div className="mt-3 flex items-center gap-2">
        <LockedAction id={`${id}-add`} label={t('editor.addItem')} reason={t('editor.fixedCountReason')} />
        <LockedAction
          id={`${id}-remove`}
          label={t('editor.removeItem')}
          reason={t('editor.fixedCountReason')}
        />
      </div>
    </div>
  );
}

/** One editable string: a label, a counter, the input, its error and the baseline. */
function TextLeaf({
  id,
  label,
  dir,
  limit,
  value,
  baseValue,
  error,
  onChange,
}: {
  id: string;
  label: string;
  dir: 'ltr' | 'rtl';
  limit: number | null;
  value: string;
  baseValue: string;
  error: string | undefined;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation('copy');
  const errorId = `${id}-error`;
  const used = runeLength(value);
  const over = limit != null && used > limit;
  const multiline = preferMultiline(baseValue) || preferMultiline(value);
  return (
    <div className={cn(multiline && 'md:col-span-2')}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label
          htmlFor={id}
          dir="ltr"
          className="font-mono text-[11px] font-semibold text-[color:var(--color-fg-muted)]"
        >
          {label}
        </label>
        <span
          className={cn(
            'text-[11px] text-[color:var(--color-fg-muted)]',
            over && 'font-semibold text-[color:var(--color-danger)]'
          )}
        >
          {limit != null
            ? t('editor.charCount', { used, limit })
            : t('editor.charCountNoLimit', { used })}
        </span>
      </div>
      {multiline ? (
        <textarea
          id={id}
          dir={dir}
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={FIELD_CLASS}
        />
      ) : (
        <input
          id={id}
          dir={dir}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          className={cn(FIELD_CLASS, 'h-11')}
        />
      )}
      {error ? (
        <p id={errorId} role="alert" className="mt-1 text-xs font-medium text-[color:var(--color-danger)]">
          {error}
        </p>
      ) : null}
      {baseValue ? (
        <p dir={dir} className="mt-1 whitespace-pre-wrap text-xs text-[color:var(--color-fg-muted)]">
          <span className="me-1 font-semibold uppercase tracking-wide">{t('editor.baselineLabel')}</span>
          {baseValue}
        </p>
      ) : null}
    </div>
  );
}

const FIELD_CLASS =
  'block w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3 text-sm text-[color:var(--color-fg)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2 aria-[invalid=true]:border-[color:var(--color-danger)]';

/**
 * Read-only join keys, collapsed. Rendered as disabled inputs rather than plain
 * text so the shape of the item stays legible — you can see there is a `key`
 * field and what is in it, you just cannot type into it.
 */
function StructuralFields({
  id,
  fields,
  item,
}: {
  id: string;
  fields: readonly CopyItemField[];
  item: Item;
}) {
  const { t } = useTranslation('copy');
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-3 border-t border-[color:var(--color-border)] pt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-[var(--radius-md)] px-1 py-1 text-xs font-semibold text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
      >
        <ChevronDown
          className={cn('size-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
        <Lock className="size-3.5" aria-hidden />
        {t('editor.structuralToggle', { count: fields.length })}
      </button>
      <div id={id} hidden={!open} className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        {fields.map((field) => {
          const inputId = `${id}-${field.name}`;
          return (
            <Tooltip key={field.name}>
              <TooltipTrigger asChild>
                <div>
                  <label
                    htmlFor={inputId}
                    dir="ltr"
                    className="mb-1 flex items-center gap-1 font-mono text-[11px] font-semibold text-[color:var(--color-fg-muted)]"
                  >
                    <Lock className="size-3" aria-hidden />
                    {field.name}
                  </label>
                  <input
                    id={inputId}
                    dir="ltr"
                    readOnly
                    tabIndex={0}
                    value={displayStructural(item[field.name])}
                    aria-describedby={`${id}-why`}
                    className="block h-10 w-full cursor-not-allowed rounded-[var(--radius-md)] border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] px-3 font-mono text-xs text-[color:var(--color-fg-muted)]"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">{t('editor.structuralReason')}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      <p id={`${id}-why`} className="sr-only">
        {t('editor.structuralReason')}
      </p>
    </div>
  );
}

/** A locked list (`cities`) reads as the comma-separated values it carries. */
function displayStructural(v: CopyItemFieldValue | undefined): string {
  if (Array.isArray(v)) return v.join(', ');
  return v ?? '';
}
